import { FC } from 'react';
import { combineLatest } from 'rxjs';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { isPresentationMode } from 'env';
import { DoqLayout, DoqWithSpeechBubble } from '@ardoq/doq';
import { DangerButton } from '@ardoq/button';
import { IconName } from '@ardoq/icons';
import {
  APIPresentationAssetAttributes,
  ArdoqId,
  ViewIds,
} from '@ardoq/api-types';
import { toPresentationAsset } from 'presentation/toPresentationAsset';
import { confirm } from '@ardoq/modal';
import { connect, spreadMap } from '@ardoq/rxbeach';
import {
  ButtonLabelLinkContainer,
  DeleteDialogMessageIcon,
  DelteDialogMesageContainer,
  DoqFaceElementContainer,
  HowToReplaceSlideMessage,
  MigrateToImprovedSuccessorMessage,
  StyledButtonLabelIcon,
  ViewDiscontinuedNoPermissionsMessage,
} from './atoms';
import {
  doDeleteSlide,
  getDoqTypeForView,
  openSlideMigrationKBArticle,
} from './util';
import { StackPageManager } from '@ardoq/stack-page-manager';
import { deprecatedViews$ } from 'views/deprecatedViews$';
import { presentationPermissionOperations } from '@ardoq/access-control';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { CanCreatorMigrateSlidesInfo } from '@ardoq/api';
import { PayloadLoadSlide } from 'presentation/viewPane/actions';
import { deprecatedSlides$ } from './deprecatedSlides$';

let suppressDeleteSlideDialogs = false;

const deleteCurrentSlide = async (
  slideId: ArdoqId,
  presentation: APIPresentationAssetAttributes
) => {
  if (suppressDeleteSlideDialogs) {
    doDeleteSlide(slideId, presentation);
    return;
  }

  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;

  const result = await confirm({
    title: t(i18nLocale, 'viewDeprecation:deleteSlide.title', 'Delete slide?'),
    text: (
      <DelteDialogMesageContainer>
        <DeleteDialogMessageIcon iconName={IconName.WARNING} />{' '}
        {t(
          i18nLocale,
          'viewDeprecation:deleteSlide.text',
          'This cannot be undone'
        )}
      </DelteDialogMesageContainer>
    ),
    confirmButtonTitle: t(
      i18nLocale,
      'viewDeprecation:deleteSlide.confirmButton',
      'Delete slide'
    ),
    cancelButtonTitle: t(
      i18nLocale,
      'viewDeprecation:deleteSlide.cancelButton',
      'Cancel'
    ),
    isConfirmButtonDanger: true,
    showRememberActionCheckbox: true,
    rememberActionCustomCheckboxText: t(
      i18nLocale,
      'viewDeprecation:deleteSlide.rememberAction',
      "Don't show this again"
    ),
  });

  if (result) {
    suppressDeleteSlideDialogs = !!result.rememberAction;
    doDeleteSlide(slideId, presentation);
  }
};

type DoqFaceMessageProps = {
  hasPermissions: boolean;
  viewHasSuccessor: boolean;
  isPresentation: boolean;
  creatorCanMigrate?: boolean;
  viewId?: ViewIds;
  i18nLocale: I18nLocale;
};
const DoqFaceMessage = ({
  hasPermissions,
  viewHasSuccessor,
  isPresentation,
  viewId,
  creatorCanMigrate,
  i18nLocale,
}: DoqFaceMessageProps) => {
  if (!isPresentation && hasPermissions) {
    return <HowToReplaceSlideMessage i18nLocale={i18nLocale} />;
  }

  if (!hasPermissions || !viewHasSuccessor) {
    return (
      <ViewDiscontinuedNoPermissionsMessage
        creatorCanMigrate={creatorCanMigrate}
        i18nLocale={i18nLocale}
      />
    );
  }

  return (
    <MigrateToImprovedSuccessorMessage
      viewId={viewId}
      i18nLocale={i18nLocale}
    />
  );
};

type DoqFaceComponentProps = {
  hasPermissions: boolean;
  isPresentation: boolean;
  slideId: ArdoqId;
  viewId?: ViewIds;
  viewHasSuccessor: boolean;
  creatorCanMigrate?: boolean;
  presentation: APIPresentationAssetAttributes;
  i18nLocale: I18nLocale;
};
const DoqFaceComponent = ({
  slideId,
  hasPermissions,
  isPresentation,
  creatorCanMigrate,
  presentation,
  viewId,
  viewHasSuccessor,
  i18nLocale,
}: DoqFaceComponentProps) => {
  const showButtons = hasPermissions && isPresentation;
  const viewIdWithFallback = viewId || ViewIds.PAGESVIEW;

  return (
    <DoqFaceElementContainer id="discontinued-doqface-element-container">
      <DoqWithSpeechBubble
        title={t(
          i18nLocale,
          'viewDeprecation:discontinuedView.title',
          'Discontinued view'
        )}
        layout={DoqLayout.PAGE}
        message={
          <DoqFaceMessage
            viewHasSuccessor={viewHasSuccessor}
            creatorCanMigrate={creatorCanMigrate}
            hasPermissions={hasPermissions}
            isPresentation={isPresentation}
            viewId={viewId}
            i18nLocale={i18nLocale}
          />
        }
        buttonLabel={
          showButtons ? (
            <ButtonLabelLinkContainer>
              {t(
                i18nLocale,
                'viewDeprecation:discontinuedView.howToMigrateButton',
                'How to migrate slides'
              )}
              <StyledButtonLabelIcon iconName={IconName.OPEN_IN_NEW} />
            </ButtonLabelLinkContainer>
          ) : undefined
        }
        doqType={getDoqTypeForView(viewIdWithFallback)}
        extraButton={
          showButtons ? (
            <DangerButton
              onClick={() => deleteCurrentSlide(slideId, presentation)}
            >
              {t(
                i18nLocale,
                'viewDeprecation:discontinuedView.deleteSlideButton',
                'Delete slide'
              )}
            </DangerButton>
          ) : undefined
        }
        onButtonClick={showButtons ? openSlideMigrationKBArticle : undefined}
      />
    </DoqFaceElementContainer>
  );
};

type DiscontinuedViewReplacementProps = {
  activePresentation: APIPresentationAssetAttributes | null;
  slidesInfo?: CanCreatorMigrateSlidesInfo;
  activeSlide?: PayloadLoadSlide;
  viewHasSuccessor: boolean;
  hasPermissions: boolean;
  i18nLocale: I18nLocale;
};

const DiscontinuedViewReplacementBase: FC<DiscontinuedViewReplacementProps> = ({
  activeSlide,
  viewHasSuccessor,
  slidesInfo,
  activePresentation,
  hasPermissions,
  i18nLocale,
}) => {
  if (!activeSlide || !activePresentation) {
    return null;
  }

  const isPresentation = isPresentationMode();

  return (
    <>
      <StackPageManager />
      <DoqFaceComponent
        creatorCanMigrate={slidesInfo?.[activeSlide.id]?.creatorCanMigrate}
        hasPermissions={hasPermissions}
        isPresentation={isPresentation}
        slideId={activeSlide.id}
        viewId={activeSlide.viewId}
        viewHasSuccessor={viewHasSuccessor}
        presentation={activePresentation}
        i18nLocale={i18nLocale}
      />
    </>
  );
};

export const DiscontinuedViewReplacement = connect(
  DiscontinuedViewReplacementBase,
  combineLatest([
    deprecatedSlides$,
    i18nLocale$,
    currentUserPermissionContext$,
    deprecatedViews$,
  ]).pipe(
    spreadMap(
      (
        { activePresentation, slidesInfo, activeSlide },
        i18nLocale,
        permissionContext,
        deprecatedViews
      ) => {
        const activePresentationAsset = activePresentation
          ? toPresentationAsset(activePresentation)
          : null;
        return {
          activePresentation: activePresentationAsset,
          slidesInfo,
          activeSlide,
          viewHasSuccessor: Boolean(
            deprecatedViews.find(({ id }) => id === activeSlide?.viewId)
              ?.deprecationSuccessor
          ),
          hasPermissions: Boolean(
            activeSlide?.hasFullAccess &&
            activePresentationAsset &&
            presentationPermissionOperations.canEditPresentation(
              permissionContext,
              activePresentationAsset
            )
          ),
          i18nLocale,
        };
      }
    )
  )
);
